import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Calendar,
  Filter,
  FileSpreadsheet,
  FilePlus,
  Search,
  ChevronLeft,
  ChevronRight,
  Users,
  Check,
  X,
  Clock,
  AlertTriangle,
  Building2,
  Eye,
  Code,
  FileUp,
  RefreshCw,
  Download
} from "lucide-react";
import Dropdown from "@/components/ui/dropdown";
import { MFOItem } from "@/types/kanban";
import MFODetailDialog from "@/components/mfo/MFODetailDialog";
import CreateMFOModal from "@/components/mfo/CreateMFOModal";
import CreateMFOCodeModal from "@/components/mfo/CreateMFOCodeModal";
import { useToast } from "@/hooks/use-toast";
import { fetchMFOs, createMFO, deleteMFO } from "@/utils/supabase/mfoUtils";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { useMFOCodes } from "@/hooks/use-mfo-codes";
import { DeleteMFOConfirmationDialog } from "@/components/mfo/DeleteMFOConfirmationDialog";
import { ImportMFOModal } from "@/components/mfo/ImportMFOModal";
import { ResetMFOConfirmationDialog } from "@/components/mfo/ResetMFOConfirmationDialog";
import * as XLSX from 'xlsx';
import * as LucideIcons from 'lucide-react';

const DIVISION_NAMES: Record<string, string> = {
  "dept-tourism": "Tourism Division",
  "dept-culture": "Cultural Affairs",
  "dept-events": "Events Management"
};

interface CreateMFOModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  departments: { id: string; name: string }[];
  employees: { id: string; name: string; avatar?: string }[];
  mfoCodes: { id: string; code: string; title: string }[];
  onMFOCreated?: (mfo: MFOItem) => Promise<void>;
}

interface MFOData {
  code: string;
  title: string;
  successIndicators: string[];
  departmentId?: string;
  dueDate: string;
  description?: string;
  budget?: number;
}

interface TaskAssignee {
  id: string;
  name: string;
}

interface DatabaseMFO {
  code: string;
  title: string;
  success_indicators: string[];
  department_id: string | null;
  due_date: string;
  description: string;
  budget: number;
  status: string;
  priority: string;
  progress: number;
  q1?: boolean;
  q2?: boolean;
  q3?: boolean;
  q4?: boolean;
  public_access_token?: string;
}

function toSlug(str) {
  return str?.toLowerCase().replace(/\s+/g, '-').trim();
}

const MFO = () => {
  const [searchParams] = useSearchParams();
  const departmentId = searchParams.get("department");
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterProgress, setFilterProgress] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMFO, setSelectedMFO] = useState<MFOItem | null>(null);
  const [isMFODialogOpen, setIsMFODialogOpen] = useState(false);
  
  const [isCreateMFOModalOpen, setIsCreateMFOModalOpen] = useState(false);
  const [isCreateMFOCodeModalOpen, setIsCreateMFOCodeModalOpen] = useState(false);
  const [mfos, setMfos] = useState<MFOItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState<{id: string, name: string}[]>([]);
  const [employees, setEmployees] = useState<{id: string, name: string, avatar?: string}[]>([]);
  const [mfoCodes, setMfoCodes] = useState<{id: string, code: string, title: string}[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [mfoToDelete, setMfoToDelete] = useState<MFOItem | null>(null);
  const itemsPerPage = 10;
  const [mfoData, setMfoData] = useState<MFOData[]>([]);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [kanbanStatuses, setKanbanStatuses] = useState<any[]>([]);

  useEffect(() => {
    const loadMFOs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("mfo_items")
          .select(`
            id,
            code,
            title,
            budget,
            priority,
            due_date,
            status,
            q1,
            q2,
            q3,
            q4,
            progress,
            department_id,
            departments (
              id,
              name
            ),
            mfo_success_indicators (
              indicator
            ),
            mfo_assignees (
              employee:employees (
                id,
                name,
                avatar
              )
            )
          `);

        if (error) throw error;

        const mfoData = data.map((mfo) => ({
          id: mfo.id,
          code: mfo.code,
          title: mfo.title,
          budget: mfo.budget,
          priority: mfo.priority,
          dueDate: mfo.due_date,
          status: mfo.status,
          progress: mfo.progress || 0,
          departmentId: mfo.department_id,
          departmentName: mfo.departments?.name,
          timeline: {
            q1: mfo.q1,
            q2: mfo.q2,
            q3: mfo.q3,
            q4: mfo.q4
          },
          successIndicators: mfo.mfo_success_indicators?.map(
            (indicator) => indicator.indicator
          ),
          collaborators: mfo.mfo_assignees?.map((assignee) => ({
            id: assignee.employee?.id,
            name: assignee.employee?.name,
            avatar: assignee.employee?.avatar
          }))
        }));

        queryClient.setQueryData(['mfoItems', departmentId || null], mfoData);
        
        setMfos(mfoData);

      } catch (error) {
        console.error("Error loading MFOs:", error);
        toast({
          title: "Error",
          description: "Failed to load MFOs",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadMFOs();

    // Set up real-time subscription for MFO progress updates
    const mfoProgressSubscription = supabase
      .channel('mfo_progress_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'mfo_items'
        },
        (payload) => {
          // Update the MFO in the list if it exists
          setMfos(prevMfos => {
            return prevMfos.map(mfo => {
              if (mfo.id === payload.new.id) {
                return {
                  ...mfo,
                  progress: payload.new.progress
                };
              }
              return mfo;
            });
          });
        }
      )
      .subscribe();

    return () => {
      mfoProgressSubscription.unsubscribe();
    };
  }, [departmentId, toast, queryClient]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const { data, error } = await supabase
          .from('departments')
          .select('id, name');
        
        if (error) throw error;
        
        if (data) {
          setDepartments(data);
          
          const divisionNames: Record<string, string> = {};
          data.forEach(dept => {
            divisionNames[dept.id] = dept.name;
          });
          
          // Merge with existing DIVISION_NAMES
          Object.keys(divisionNames).forEach(key => {
            DIVISION_NAMES[key] = divisionNames[key];
          });
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    
    fetchDepartments();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data, error } = await supabase
          .from('employees')
          .select('id, name, avatar');
        
        if (error) throw error;
        
        if (data) {
          setEmployees(data);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    
    fetchEmployees();
  }, []);

  useEffect(() => {
    const fetchMFOCodes = async () => {
      try {
      
        const { data: mfoCodesData, error } = await supabase
          .from('mfo_codes' as any)
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        if (mfoCodesData) {
          // Cast data to any first to avoid TypeScript errors
          const formattedCodes = (mfoCodesData as any[]).map(item => ({
            id: item.id,
            code: item.code,
            title: item.title
          }));
          
          setMfoCodes(formattedCodes);
        }
      } catch (error) {
        console.error("Error fetching MFO codes:", error);
      }
    };
    
    fetchMFOCodes();
  }, []);

  useEffect(() => {
    // Fetch kanban columns for statuses
    const fetchKanbanStatuses = async () => {
      try {
        const { data, error } = await supabase
          .from('kanban_columns')
          .select('id, title, icon_color, icon_name');
        if (error) throw error;
        setKanbanStatuses(data || []);
      } catch (error) {
        console.error("Error fetching kanban statuses:", error);
      }
    };
    fetchKanbanStatuses();
  }, []);

  const statusOptions = useMemo(() => [
    { label: "All Statuses", value: "all" },
    ...kanbanStatuses.map(col => {
      const Icon = LucideIcons[col.icon_name] || LucideIcons["Circle"];
      return {
        label: col.title,
        value: toSlug(col.title),
        icon: <Icon className="h-4 w-4 mr-2" style={{ color: col.icon_color }} />,
      };
    })
  ], [kanbanStatuses]);

  const priorityOptions = [
    { label: "All Priority", value: "all" },
    { 
      label: "High", 
      value: "high",
      icon: <span className="h-2 w-2 rounded-full bg-red-500 mr-2" />
    },
    { 
      label: "Medium", 
      value: "medium",
      icon: <span className="h-2 w-2 rounded-full bg-amber-500 mr-2" />
    },
    { 
      label: "Low", 
      value: "low",
      icon: <span className="h-2 w-2 rounded-full bg-blue-500 mr-2" />
    },
  ];

  const progressOptions = [
    { label: "All Progress", value: "all" },
    { label: "0-25%", value: "0-25" },
    { label: "26-50%", value: "26-50" },
    { label: "51-75%", value: "51-75" },
    { label: "76-100%", value: "76-100" },
  ];

  const filteredMFOs = useMemo(() => {
    return mfos.filter(mfo => {
      if (departmentId && mfo.departmentId !== departmentId) {
        return false;
      }
      if (
        filterStatus !== "all" &&
        mfo.status !== filterStatus
      ) {
        return false;
      }
      
      if (filterPriority !== "all" && mfo.priority !== filterPriority) {
        return false;
      }
      
      if (filterProgress !== "all") {
        const progress = mfo.progress || 0;
        if (filterProgress === "0-25" && !(progress >= 0 && progress <= 25)) return false;
        if (filterProgress === "26-50" && !(progress > 25 && progress <= 50)) return false;
        if (filterProgress === "51-75" && !(progress > 50 && progress <= 75)) return false;
        if (filterProgress === "76-100" && !(progress > 75 && progress <= 100)) return false;
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          mfo.title.toLowerCase().includes(query) ||
          mfo.code.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  }, [departmentId, filterStatus, filterPriority, filterProgress, searchQuery, mfos]);

  const paginatedMFOs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredMFOs.slice(startIndex, endIndex);
  }, [currentPage, filteredMFOs, itemsPerPage]);

  const handleCreateMFO = async (mfo: MFOItem) => {
    try {
      await createMFO({
        code: mfo.code,
        title: mfo.title,
        departmentId: mfo.departmentId,
        budget: mfo.budget,
        priority: mfo.priority,
        dueDate: mfo.dueDate,
        status: mfo.status,
        successIndicators: mfo.successIndicators,
        timeline: mfo.timeline,
        collaborators: mfo.collaborators,
        progress: mfo.progress || 0
      });
      
      const updatedMFOs = await fetchMFOs(departmentId || undefined);
      
      const completeData = updatedMFOs.map(mfo => ({
        ...mfo,
        progress: mfo.progress ?? 0
      }));
      
      setMfos(completeData);
      
      toast({
        title: "MFO Created",
        description: `Successfully created ${mfo.code}: ${mfo.title}`,
      });

      setIsCreateMFOModalOpen(false); // Explicitly close the modal
    } catch (error) {
      console.error("Error creating MFO:", error);
      toast({
        title: "Error",
        description: "Failed to create MFO",
        variant: "destructive",
      });
    }
  };

  const handleCreateMFOCode = async (data: { 
    code: string; 
    title: string; 
    budget: number; 
    collaborators: TaskAssignee[];
  }) => {
    try {
      const newMfoData = [...mfoData, { 
        code: data.code, 
        title: data.title, 
        successIndicators: [], 
        departmentId: departmentId || undefined,
        dueDate: new Date().toISOString().split('T')[0],
        description: '',
        budget: data.budget
      }];
      setMfoData(newMfoData);

      // Insert MFOs into the database
      const databaseMFOs: DatabaseMFO[] = newMfoData.map(mfo => ({
        code: mfo.code,
        title: mfo.title,
        success_indicators: mfo.successIndicators,
        department_id: mfo.departmentId || null,
        due_date: mfo.dueDate,
        description: mfo.description || '',
        budget: mfo.budget || 0,
        status: 'not-started',
        priority: 'medium',
        progress: 0,
        q1: false,
        q2: false,
        q3: false,
        q4: false
      }));

      const { error } = await supabase
        .from('mfo_items')
        .insert(databaseMFOs);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "MFO created successfully",
      });
      
      setMfoData([]);
    } catch (error) {
      console.error('Error creating MFO:', error);
      toast({
        title: "Error",
        description: "Failed to create MFO",
        variant: "destructive",
      });
    }
  };

  const openMFODialog = (mfo: MFOItem) => {
    setSelectedMFO(mfo);
    setIsMFODialogOpen(true);
  };

  const handleViewMFO = (mfo: MFOItem) => {
    navigate(`/dashboard/mfo/${mfo.id}`);
  };

  const handleDeleteMFO = async (mfoId: string) => {
    try {
      await deleteMFO(mfoId);
      
      setMfos(prev => prev.filter(mfo => mfo.id !== mfoId));
      
      toast({
        title: "MFO Deleted",
        description: "MFO has been successfully deleted",
      });
    } catch (error) {
      console.error("Error deleting MFO:", error);
      toast({
        title: "Error",
        description: "Failed to delete MFO",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (mfo: MFOItem) => {
    setMfoToDelete(mfo);
    setIsDeleteDialogOpen(true);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > Math.ceil(filteredMFOs.length / itemsPerPage)) return;
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(filteredMFOs.length / itemsPerPage);

  const handleResetMFOs = async () => {
    try {
      const { error } = await supabase
        .from('mfo_items')
        .delete()
        .neq('id', 'dummy'); // This ensures we delete all records
      
      if (error) throw error;
      
      setMfos([]);
      
      toast({
        title: "MFOs Reset",
        description: "All MFOs have been successfully reset",
      });
    } catch (error) {
      console.error("Error resetting MFOs:", error);
      toast({
        title: "Error",
        description: "Failed to reset MFOs",
        variant: "destructive",
      });
    }
  };

  const handleExportToExcel = () => {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    
    // Get current year
    const currentYear = new Date().getFullYear();
    
    // Prepare data for export
    const exportData = filteredMFOs.map(mfo => ({
      'MFO Code': mfo.code,
      'Title': mfo.title,
      'Division': DIVISION_NAMES[mfo.departmentId] || mfo.departmentId,
      'Status': mfo.status.replace('-', ' ').toUpperCase(),
      'Priority': mfo.priority.toUpperCase(),
      'Progress': `${mfo.progress}%`,
      'Due Date': mfo.dueDate,
      'Budget': `₱${mfo.budget?.toLocaleString()}`,
      'Success Indicators': mfo.successIndicators?.join('\n') || '',
      'Collaborators': mfo.collaborators?.map(c => c.name).join(', ') || '',
      'Timeline': [
        mfo.timeline.q1 ? 'Q1' : '',
        mfo.timeline.q2 ? 'Q2' : '',
        mfo.timeline.q3 ? 'Q3' : '',
        mfo.timeline.q4 ? 'Q4' : ''
      ].filter(Boolean).join(', ')
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add title row
    XLSX.utils.sheet_add_aoa(ws, [['DPCR ' + currentYear]], { origin: 'A1' });
    
    // Merge cells for title
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 10 } }];
    
    // Style the title cell
    ws['A1'] = {
      v: 'DPCR ' + currentYear,
      s: {
        font: { sz: 16, bold: true },
        alignment: { horizontal: 'center', vertical: 'center' }
      }
    };

    // Add headers
    const headers = Object.keys(exportData[0]);
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A2' });

    // Style headers
    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 1, c: index });
      ws[cellRef].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "CCCCCC" } },
        alignment: { horizontal: 'center' }
      };
    });

    // Set column widths
    const colWidths = [
      { wch: 15 }, // MFO Code
      { wch: 40 }, // Title
      { wch: 20 }, // Division
      { wch: 15 }, // Status
      { wch: 15 }, // Priority
      { wch: 10 }, // Progress
      { wch: 15 }, // Due Date
      { wch: 15 }, // Budget
      { wch: 50 }, // Success Indicators
      { wch: 30 }, // Collaborators
      { wch: 15 }  // Timeline
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'MFO Progress Report');

    // Generate Excel file
    XLSX.writeFile(wb, `DPCR_${currentYear}_MFO_Report.xlsx`);
  };

  function getKanbanColumnByStatus(status) {
    return kanbanStatuses.find(col => toSlug(col.title) === status);
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      <div className="flex justify-between items-center">
        {departmentId ? (
          <div className="flex items-center">
            <Button 
              variant="outline" 
              size="sm"
              className="mr-3"
              onClick={() => navigate("/dashboard/divisions")}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h2 className="text-3xl font-bold tracking-tight flex items-center">
              <Building2 className="h-6 w-6 mr-2 text-primary" />
              {DIVISION_NAMES[departmentId]} MFOs
            </h2>
          </div>
        ) : (
          <h2 className="text-3xl font-bold tracking-tight">Major Final Outputs</h2>
        )}
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleExportToExcel}>
            <Download className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>
          <ImportMFOModal />
          <Button variant="outline" onClick={() => setIsCreateMFOCodeModalOpen(true)}>
            <Code className="mr-2 h-4 w-4" />
            Create MFO Code
          </Button>
          <Button variant="outline" onClick={() => setIsResetDialogOpen(true)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset MFOs
          </Button>
          <Button onClick={() => setIsCreateMFOModalOpen(true)}>
            <FilePlus className="mr-2 h-4 w-4" />
            Create MFO
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border shadow-sm animate-fade-in">
        <div className="p-4 border-b flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search MFOs..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Dropdown 
              label="Status"
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              className="min-w-[140px]"
            />
            <Dropdown
              label="Priority"
              options={priorityOptions}
              value={filterPriority}
              onChange={setFilterPriority}
              className="min-w-[140px]"
            />
            <Dropdown
              label="Progress"
              options={progressOptions}
              value={filterProgress}
              onChange={setFilterProgress}
              className="min-w-[140px]"
            />
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => navigate("/dashboard/calendar")}
            >
              <Calendar className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading && (
          <div className="p-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        )}

        {!loading && !departmentId && !filteredMFOs.length && (
          <div className="p-6 text-center">
            <p className="text-muted-foreground">
              Please select a division to view its MFOs.
            </p>
            <Button 
              className="mt-2" 
              onClick={() => navigate("/dashboard/departments")}
            >
              View Divisions
            </Button>
          </div>
        )}

        {!loading && departmentId && !filteredMFOs.length && (
          <div className="p-6 text-center">
            <p className="text-muted-foreground">
              No MFOs found for this division. Create a new MFO to get started.
            </p>
            <Button 
              className="mt-2" 
              onClick={() => setIsCreateMFOModalOpen(true)}
            >
              Create New MFO
            </Button>
          </div>
        )}

        {!loading && filteredMFOs.length > 0 && (
          <>
            <div className="overflow-x-auto max-w-[calc(100vw-64px-3rem)]">
              <table className="w-full">
                <thead>
                  <tr className="border-b transition-colors hover:bg-muted/50">
                    <th className="text-left p-4 text-sm font-medium">Code</th>
                    <th className="text-left p-4 text-sm font-medium">Title</th>
                    {!departmentId && (
                      <th className="text-left p-4 text-sm font-medium">Division</th>
                    )}
                    <th className="text-left p-4 text-sm font-medium">Status</th>
                    <th className="text-left p-4 text-sm font-medium">Priority</th>
                    <th className="text-left p-4 text-sm font-medium">Progress</th>
                    <th className="text-left p-4 text-sm font-medium">Due Date</th>
                    <th className="text-left p-4 text-sm font-medium">Employees</th>
                    <th className="text-left p-4 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMFOs.map((mfo) => (
                    <tr
                      key={mfo.id}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      <td className="p-4 align-middle">
                        <div className="font-medium">{mfo.code}</div>
                      </td>
                      <td className="p-4 align-middle">
                        <div className="font-medium">{mfo.title}</div>
                      </td>
                      {!departmentId && (
                        <td className="p-4 align-middle">
                          <div className="flex items-center">
                            <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                            <span>{DIVISION_NAMES[mfo.departmentId] || mfo.departmentId}</span>
                          </div>
                        </td>
                      )}
                      <td className="p-4 align-middle">
                        <div className="flex items-center">
                          {(() => {
                            const col = getKanbanColumnByStatus(mfo.status);
                            const Icon = col && LucideIcons[col.icon_name] ? LucideIcons[col.icon_name] : LucideIcons["Circle"];
                            const color = col?.icon_color || "#64748b";
                            return (
                              <span
                                className="flex items-center px-2 py-1 rounded-full text-xs"
                                style={{
                                  color,
                                  backgroundColor: color + "20" // light background, adjust as needed
                                }}
                              >
                                <Icon className="h-3 w-3 mr-1" />
                                {col ? col.title : mfo.status}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center">
                          {mfo.priority === "high" && (
                            <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
                              <span className="h-2 w-2 rounded-full bg-red-500" />
                              High
                            </span>
                          )}
                          {mfo.priority === "medium" && (
                            <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-1 rounded-full text-xs">
                              <span className="h-2 w-2 rounded-full bg-amber-500" />
                              Medium
                            </span>
                          )}
                          {mfo.priority === "low" && (
                            <span className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded-full text-xs">
                              <span className="h-2 w-2 rounded-full bg-blue-500" />
                              Low
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 align-middle">
                        <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              mfo.progress < 30
                                ? "bg-red-500"
                                : mfo.progress < 70
                                ? "bg-amber-500"
                                : "bg-green-500"
                            }`}
                            style={{ width: `${mfo.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground mt-1 block">
                          {mfo.progress}%
                        </span>
                      </td>
                      <td className="p-4 align-middle text-sm">
                        {mfo.dueDate}
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span>{mfo.collaborators?.length || 0}</span>
                        </div>
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="flex items-center"
                            onClick={() => handleViewMFO(mfo)}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/dashboard/mfo/${mfo.id}/edit`)}
                          >
                            Edit
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteClick(mfo)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {paginatedMFOs.length} of {filteredMFOs.length} MFOs
                {departmentId && ` for ${DIVISION_NAMES[departmentId] || departmentId}`}
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button 
                    key={i + 1} 
                    variant={currentPage === i + 1 ? "default" : "outline"} 
                    size="sm" 
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <MFODetailDialog 
        mfo={selectedMFO} 
        isOpen={isMFODialogOpen} 
        onOpenChange={setIsMFODialogOpen} 
      />
      
      <CreateMFOModal 
        isOpen={isCreateMFOModalOpen}
        onOpenChange={setIsCreateMFOModalOpen}
        departments={departments}
        employees={employees}
        mfoCodes={mfoCodes}
        onMFOCreated={handleCreateMFO}
      />
      
      <CreateMFOCodeModal 
        isOpen={isCreateMFOCodeModalOpen}
        onOpenChange={setIsCreateMFOCodeModalOpen}
        onSave={handleCreateMFOCode}
        mode="create"
      />

      <DeleteMFOConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        mfo={mfoToDelete}
        onConfirm={handleDeleteMFO}
      />

      <ResetMFOConfirmationDialog
        isOpen={isResetDialogOpen}
        onOpenChange={setIsResetDialogOpen}
        onConfirm={handleResetMFOs}
      />
    </div>
  );
};

export default MFO;

